import { useCallback, useEffect, useState } from "react";
import { getOperation } from "../services/api";
import type { IOperationResponse } from "../services/api";
import { useOperationSSE } from "./useOperationSSE";

export type WatchedOperationStatus = "loading" | "ready" | "missing";

export interface UseWatchedOperationResult {
  operation: IOperationResponse | null;
  status: WatchedOperationStatus;
  refresh: () => Promise<void>;
}

/**
 * Loads and live-tracks a single operation for the receipt screen, which
 * renders on its own route outside the transactions list that normally owns
 * the SSE stream.
 */
export function useWatchedOperation(
  linkId: string | null | undefined,
  operationId: string | null | undefined
): UseWatchedOperationResult {
  const [operation, setOperation] = useState<IOperationResponse | null>(null);
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  const upsertOperation = useCallback(
    (op: IOperationResponse) => {
      if (op.operationId === operationId) {
        setOperation(op);
      }
    },
    [operationId]
  );

  useOperationSSE(
    linkId,
    operation ? [operation] : [],
    upsertOperation,
    undefined,
    { watchOperationId: operationId ?? undefined }
  );

  useEffect(() => {
    if (!linkId || !operationId) {
      setOperation(null);
      setInitialLoadDone(false);
      return;
    }

    let cancelled = false;
    setInitialLoadDone(false);
    setOperation(null);

    getOperation(linkId, operationId)
      .then((op) => {
        if (!cancelled) setOperation(op);
      })
      .catch((error) => {
        console.error("[useWatchedOperation] Failed to fetch operation:", error);
      })
      .finally(() => {
        if (!cancelled) setInitialLoadDone(true);
      });

    return () => {
      cancelled = true;
    };
  }, [linkId, operationId]);

  let status: WatchedOperationStatus;
  if (operation) {
    status = "ready";
  } else if (!initialLoadDone) {
    status = "loading";
  } else {
    status = "missing";
  }

  const refresh = useCallback(async () => {
    if (!linkId || !operationId) return;
    const op = await getOperation(linkId, operationId);
    setOperation(op);
  }, [linkId, operationId]);

  return { operation, status, refresh };
}
